import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { fetchSuspiciousReportUser, fetchSuspiciousManagerReportUser } from "actions/Reports";
import { dateTimeFormatter } from "utilities";
import ButtonContained from "components/ButtonContained";
import TableNew from "components/TableNew";
import LoadingIndicator from "components/LoadingIndicator";

const UserSettingsReportsModal = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const authUser = useSelector(state => state.auth.authUser);
  const user = useSelector(state => state.users.userObject);
  const site = useSelector(state => state.auth.site);
  const userTimezone = useSelector(state => state.app.userTimezone);
  const suspiciousReportUser = useSelector(state => state.reports.suspiciousReportUser);
  const suspiciousManagerReportUser = useSelector(state => state.reports.suspiciousManagerReportUser);
  const [period, setPeriod] = useState("week");

  const fetchData = (period) => {
    dispatch(fetchSuspiciousReportUser({ period: period, userUUID: user.uuid }));
    authUser?.permissions?.includes('view_suspicious_manager') &&
      dispatch(fetchSuspiciousManagerReportUser({ period: period, userUUID: user.uuid }));
  }

  useEffect(() => {
    if (!user) return;
    fetchData(period);
  }, [user, authUser]);

  const handleSetWeek = () => {
    setPeriod("week");
    fetchData("week");
  }

  const handleSetMonth = () => {
    setPeriod("month");
    fetchData("month");
  }

  return (
    <div className="min-h-[300px] max-h-[70vh] flex flex-col px-10 space-y-8">
      <div>
        <h5 className="tw-heading-5">{intl.formatMessage({ id: "pages.reports.Types" })}</h5>
      </div>
      <div className="flex justify-start gap-2">
        <ButtonContained
          id="User-Settings-Period-Week-Button"
          dataCy="User-Settings-Period-Week-Button"
          text={intl.formatMessage({ id: "comp.reports.week" })}
          type={period === "week" ? "default" : "inactive"}
          onClick={() => period === "month" && handleSetWeek()}
        />
        <ButtonContained
          id="User-Settings-Period-Month-Button"
          dataCy="User-Settings-Period-Month-Button"
          text={intl.formatMessage({ id: "comp.reports.month" })}
          type={period === "month" ? "default" : "inactive"}
          onClick={() => period === "week" && handleSetMonth()}
        />
      </div>
      {suspiciousReportUser &&
        (!authUser?.permissions?.includes("view_suspicious_manager") || suspiciousManagerReportUser)
        ? <div className="overflow-auto custom-scrollbar pr-2">
          <div className="grid grid-cols-2 gap-8">

            {/* Out of Zone Attempts */}

            <div>
              <h5 className="tw-heading-5">
                {intl.formatMessage({ id: "comp.reports.outOfZone" })}
              </h5>
              <TableNew
                data={suspiciousReportUser.attemptedOutOfZone || []}
                defaultSortKey="attemptedAt"
                noPadding={true}
                maxHeight="max-h-[200px]"
                columns={[
                  {
                    key: "entryName",
                    label: intl.formatMessage({ id: "pages.entry.Type" }),
                    sortable: true,
                    render: (e) => (
                      <span
                        className="tw-desc-reg font-semibold color-[var(--color-gray-800)]"
                      >
                        {e.entryName && e.entryName !== ""
                          ? e.entryName
                          : intl.formatMessage({ id: 'form.na' })
                        }
                      </span>
                    )
                  },
                  {
                    key: "attemptedAt",
                    label: intl.formatMessage({ id: "comp.reports.timeAttempt" }),
                    sortable: true,
                    align: "justify-end",
                    width: "100px",
                    render: (e) => (
                      <span
                        className="tw-desc-reg font-semibold color-[var(--color-gray-800)]"
                      >
                        {e.attemptedAt && e.attemptedAt !== ""
                          ? dateTimeFormatter(e.attemptedAt, authUser, site, userTimezone)
                          : intl.formatMessage({ id: 'form.na' })
                        }
                      </span>
                    )
                  }
                ]}
              />
            </div>

            {/* Did Not Go To Unit */}

            <div>
              <h5 className="tw-heading-5">
                {intl.formatMessage({ id: "comp.reports.notUnit" })}
              </h5>
              <TableNew
                data={suspiciousReportUser.didNotGoToUnit || []}
                defaultSortKey="entryFirstOpenedAt"
                noPadding={true}
                maxHeight="max-h-[200px]"
                columns={[
                  {
                    key: "entryFirstOpenedAt",
                    label: intl.formatMessage({ id: "comp.reports.timeEntry" }),
                    sortable: true,
                    render: (e) => (
                      <span
                        className="tw-desc-reg font-semibold color-[var(--color-gray-800)]"
                      >
                        {e.entryFirstOpenedAt && e.entryFirstOpenedAt !== ""
                          ? dateTimeFormatter(e.entryFirstOpenedAt, authUser, site, userTimezone)
                          : intl.formatMessage({ id: 'form.na' })
                        }
                      </span>
                    )
                  }
                ]}
              />
            </div>
            
            {/* Did Not Leave Facility */}

            <div>
              <h5 className="tw-heading-5">
                {intl.formatMessage({ id: "comp.reports.notLeave" })}
              </h5>
              <TableNew
                data={suspiciousReportUser.didNotLeaveAfterLockingUnit || []}
                defaultSortKey="entryFirstOpenedAt"
                noPadding={true}
                maxHeight="max-h-[200px]"
                columns={[
                  {
                    key: "entryFirstOpenedAt",
                    label: intl.formatMessage({ id: "comp.reports.timeEntry" }),
                    sortable: true,
                    render: (e) => (
                      <span
                        className="tw-desc-reg font-semibold color-[var(--color-gray-800)]"
                      >
                        {e.entryFirstOpenedAt && e.entryFirstOpenedAt !== ""
                          ? dateTimeFormatter(e.entryFirstOpenedAt, authUser, site, userTimezone)
                          : intl.formatMessage({ id: 'form.na' })
                        }
                      </span>
                    )
                  }
                ]}
              />
            </div>

            {/* Multiple Entries */}

            <div>
              <h5 className="tw-heading-5">
                {intl.formatMessage({ id: "comp.reports.multipleEntry" })}
              </h5>
              <TableNew
                data={suspiciousReportUser.multipleEntries || []}
                defaultSortKey="entryFirstOpenedAt"
                noPadding={true}
                maxHeight="max-h-[200px]"
                columns={[
                  {
                    key: "entryCount",
                    label: intl.formatMessage({ id: "comp.reports.numEntry" }),
                    sortable: true,
                    render: (e) => (
                      <span
                        className="tw-desc-reg font-semibold color-[var(--color-gray-800)]"
                      >
                        {e.entryCount}
                      </span>
                    )
                  },
                  {
                    key: "entryFirstOpenedAt",
                    label: intl.formatMessage({ id: "comp.reports.timeEntry" }),
                    sortable: true,
                    align: "justify-end",
                    width: "100px",
                    render: (e) => (
                      <span
                        className="tw-desc-reg font-semibold color-[var(--color-gray-800)]"
                      >
                        {e.entryFirstOpenedAt && e.entryFirstOpenedAt !== ""
                          ? dateTimeFormatter(e.entryFirstOpenedAt, authUser, site, userTimezone)
                          : intl.formatMessage({ id: 'form.na' })
                        }
                      </span>
                    )
                  }
                ]}
              />
            </div>

            {/* Overlock Attempts */}

            <div>
              <h5 className="tw-heading-5">
                {intl.formatMessage({ id: "comp.reports.overlockAttempt" })}
              </h5>
              <TableNew
                data={suspiciousReportUser.overlockAccessAttempt || []}
                defaultSortKey="attemptedAt"
                noPadding={true}
                maxHeight="max-h-[200px]"
                columns={[
                  {
                    key: "entryName",
                    label: intl.formatMessage({ id: "pages.entry.Type" }),
                    sortable: true,
                    render: (e) => (
                      <span
                        className="tw-desc-reg font-semibold color-[var(--color-gray-800)]"
                      >
                        {e.entryName && e.entryName !== ""
                          ? e.entryName
                          : intl.formatMessage({ id: 'form.na' })
                        }
                      </span>
                    )
                  },
                  {
                    key: "attemptedAt",
                    label: intl.formatMessage({ id: "comp.reports.timeAttempt" }),
                    sortable: true,
                    align: "justify-end",
                    width: "100px",
                    render: (e) => (
                      <span
                        className="tw-desc-reg font-semibold color-[var(--color-gray-800)]"
                      >
                        {e.attemptedAt && e.attemptedAt !== ""
                          ? dateTimeFormatter(e.attemptedAt, authUser, site, userTimezone)
                          : intl.formatMessage({ id: 'form.na' })
                        }
                      </span>
                    )
                  }
                ]}
              />
            </div>
            
            {/* Late Night Access */}

            <div>
              <h5 className="tw-heading-5">
                {intl.formatMessage({ id: "comp.reports.lateAccess" })}
              </h5>
              <TableNew
                data={suspiciousReportUser.lateNightAccess || []}
                defaultSortKey="accessedAt"
                noPadding={true}
                maxHeight="max-h-[200px]"
                columns={[
                  {
                    key: "accessedAt",
                    label: intl.formatMessage({ id: "comp.reports.timeAccess" }),
                    sortable: true,
                    render: (e) => (
                      <span
                        className="tw-desc-reg font-semibold color-[var(--color-gray-800)]"
                      >
                        {e.accessedAt && e.accessedAt !== ""
                          ? dateTimeFormatter(e.accessedAt, authUser, site, userTimezone)
                          : intl.formatMessage({ id: 'form.na' })
                        }
                      </span>
                    )
                  }
                ]}
              />
            </div>
          </div>
          {authUser?.permissions?.includes('view_suspicious_manager') && <div className="mt-8 flex flex-col gap-8">

            {/* Manually Changing Status */}

            <div>
              <h5 className="tw-heading-5">
                {intl.formatMessage({ id: "comp.reports.manuallyChangingStatus" })}
              </h5>
              <TableNew
                data={suspiciousManagerReportUser.manuallyChangingStatus || []}
                defaultSortKey="updatedAt"
                noPadding={true}
                maxHeight="max-h-[200px]"
                columns={[
                  {
                    key: "unitName",
                    label: intl.formatMessage({ id: "pages.units.Type" }),
                    sortable: true,
                    render: (e) => (
                      <span
                        className="tw-desc-reg font-semibold color-[var(--color-gray-800)]"
                      >
                        {e.unitName && e.unitName !== ""
                          ? e.unitName
                          : intl.formatMessage({ id: 'form.na' })
                        }
                      </span>
                    )
                  },
                  {
                    key: "oldState",
                    label: intl.formatMessage({ id: "comp.reports.oldState" }),
                    sortable: true,
                    render: (e) => (
                      <span
                        className="tw-desc-reg font-semibold color-[var(--color-gray-800)]"
                      >
                        {e.oldState && e.oldState !== ""
                          ? e.oldState
                          : intl.formatMessage({ id: 'form.na' })
                        }
                      </span>
                    )
                  },
                  {
                    key: "newState",
                    label: intl.formatMessage({ id: "comp.reports.newState" }),
                    sortable: true,
                    render: (e) => (
                      <span
                        className="tw-desc-reg font-semibold color-[var(--color-gray-800)]"
                      >
                        {e.newState && e.newState !== ""
                          ? e.newState
                          : intl.formatMessage({ id: 'form.na' })
                        }
                      </span>
                    )
                  },
                  {
                    key: "updatedAt",
                    label: intl.formatMessage({ id: "form.updated" }),
                    sortable: true,
                    align: "justify-end",
                    width: "100px",
                    render: (e) => (
                      <span
                        className="tw-desc-reg font-semibold color-[var(--color-gray-800)]"
                      >
                        {e.updatedAt && e.updatedAt !== ""
                          ? dateTimeFormatter(e.updatedAt, authUser, site, userTimezone)
                          : intl.formatMessage({ id: 'form.na' })
                        }
                      </span>
                    )
                  }
                ]}
              />
            </div>

            {/* Overriding Occupied Units */}

            <div>
              <h5 className="tw-heading-5">
                {intl.formatMessage({ id: "comp.reports.overridingOccupiedUnits" })}
              </h5>
              <TableNew
                data={suspiciousManagerReportUser.overridingOccupiedUnits || []}
                defaultSortKey="accessedAt"
                noPadding={true}
                maxHeight="max-h-[200px]"
                columns={[
                  {
                    key: "unitName",
                    label: intl.formatMessage({ id: "pages.units.Type" }),
                    sortable: true,
                    render: (e) => (
                      <span
                        className="tw-desc-reg font-semibold color-[var(--color-gray-800)]"
                      >
                        {e.unitName && e.unitName !== ""
                          ? e.unitName
                          : intl.formatMessage({ id: 'form.na' })
                        }
                      </span>
                    )
                  },
                  {
                    key: "overrideReason",
                    label: intl.formatMessage({ id: "comp.reports.overrideReason" }),
                    sortable: true,
                    render: (e) => (
                      <span
                        className="tw-desc-reg font-semibold color-[var(--color-gray-800)]"
                      >
                        {e.overrideReason}
                      </span>
                    )
                  },
                  {
                    key: "unitUserName",
                    label: intl.formatMessage({ id: "comp.reports.unitUsername" }),
                    sortable: true,
                    render: (e) => (
                      <span
                        className="tw-desc-reg font-semibold color-[var(--color-gray-800)]"
                      >
                        {e.unitUserName}
                      </span>
                    )
                  },
                  {
                    key: "accessedAt",
                    label: intl.formatMessage({ id: "form.updated" }),
                    sortable: true,
                    align: "justify-end",
                    width: "100px",
                    render: (e) => (
                      <span
                        className="tw-desc-reg font-semibold color-[var(--color-gray-800)]"
                      >
                        {e.accessedAt && e.accessedAt !== ""
                          ? dateTimeFormatter(e.accessedAt, authUser, site, userTimezone)
                          : intl.formatMessage({ id: 'form.na' })
                        }
                      </span>
                    )
                  }
                ]}
              />
            </div>
          </div>}
        </div>
        : <div className="flex-1 flex items-center justify-center">
          <LoadingIndicator />
        </div>
      }
    </div>
  )
};

export default UserSettingsReportsModal;